import React from 'react';
import { Heart, Ambulance, Building2, Plus, X, Edit, Trash2, Printer, Clock, ClipboardList } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { writeSanitizedHTML } from '../utils/safePrint';

const ICS206MedicalPlan = ({
  theme,
  currentExercise,
  // Med Aid Stations
  medAidStations,
  currentMedAidStation,
  setCurrentMedAidStation,
  editingMedAidStation,
  addMedAidStation,
  editMedAidStation,
  cancelEditMedAidStation,
  deleteMedAidStation,
  // Med Transport
  medTransports,
  currentMedTransport,
  setCurrentMedTransport,
  editingMedTransport,
  addMedTransport,
  editMedTransport,
  cancelEditMedTransport,
  deleteMedTransport,
  // Hospitals
  hospitals,
  currentHospital,
  setCurrentHospital,
  editingHospital,
  hospitalValidation,
  addHospital,
  editHospital,
  cancelEditHospital,
  deleteHospital,
  // Medical Plan Entries (title / from / to / plan)
  medPlanEntries = [],
  currentMedPlanEntry = { title: '', fromDateTime: '', toDateTime: '', plan: '' },
  setCurrentMedPlanEntry = () => {},
  editingMedPlanEntry = null,
  addMedPlanEntry = () => {},
  editMedPlanEntry = () => {},
  cancelEditMedPlanEntry = () => {},
  deleteMedPlanEntry = () => {},
}) => {
  const serviceLevels = ['BLS', 'ALS', 'Critical Care', 'Neonatal', 'Other'];
  const traumaLevels = ['Level I', 'Level II', 'Level III', 'Level IV', 'Level V', 'N/A'];

  const exerciseName = currentExercise?.exercise_name || 'N/A';
  const startDate = currentExercise?.start_date
    ? new Date(currentExercise.start_date).toLocaleString()
    : 'Not set';
  const endDate = currentExercise?.end_date
    ? new Date(currentExercise.end_date).toLocaleString()
    : 'Not set';

  // Format an ISO/<input type=datetime-local> value to "YYYY-MM-DD HH:mm" 24h
  const fmt24 = (val) => {
    if (!val) return '—';
    // value comes from <input type="datetime-local"> as "YYYY-MM-DDTHH:mm"
    return val.replace('T', ' ').slice(0, 16);
  };

  const escapeHtml = (s) =>
    String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  // Print ICS 206
  const printICS206 = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const now = new Date().toLocaleString();

    const printContent = `<!DOCTYPE html>
<html><head><title>ICS 206 - Medical Plan</title>
<style>
  body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.4; }
  .header { text-align: center; margin-bottom: 25px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
  .header h1 { color: #f97316; margin: 0 0 8px 0; font-size: 22px; }
  .header p { margin: 3px 0; font-size: 13px; }
  h2 { color: #f97316; font-size: 16px; margin: 25px 0 10px; border-bottom: 2px solid #f97316; padding-bottom: 5px; }
  table { width: 100%; border-collapse: collapse; margin: 10px 0 20px; font-size: 12px; }
  th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
  th { background-color: #f97316; color: white; font-weight: bold; }
  tr:nth-child(even) { background-color: #f9f9f9; }
  .yes { color: #16a34a; font-weight: bold; }
  .no { color: #6b7280; }
  .badge { display: inline-block; padding: 2px 6px; border-radius: 4px; font-size: 10px; margin: 1px; }
  .badge-blue { background: #dbeafe; color: #1d4ed8; }
  .badge-purple { background: #ede9fe; color: #7c3aed; }
  .empty { text-align: center; padding: 20px; color: #999; font-style: italic; }
  .footer { margin-top: 30px; text-align: center; font-size: 11px; color: #666; border-top: 2px solid #ddd; padding-top: 15px; }
  @media print { body { margin: 0; padding: 15px; } }
</style></head><body>
  <div class="header">
    <h1>ICS 206 - MEDICAL PLAN</h1>
    <p><strong>Exercise:</strong> ${exerciseName}</p>
    <p><strong>Operational Period:</strong> ${startDate} to ${endDate}</p>
    <p><strong>Generated:</strong> ${now}</p>
  </div>

  <h2>Medical Aid Stations (${medAidStations.length})</h2>
  ${medAidStations.length > 0 ? `<table>
    <thead><tr><th>Station Name</th><th>Location</th><th>Contact</th><th>Paramedics On Site</th></tr></thead>
    <tbody>${medAidStations.map(s => `<tr>
      <td>${s.station || 'N/A'}</td><td>${s.location || 'N/A'}</td><td>${s.contact || 'N/A'}</td>
      <td class="${s.paramedics === 'Yes' ? 'yes' : 'no'}">${s.paramedics || 'No'}</td>
    </tr>`).join('')}</tbody>
  </table>` : '<div class="empty">No medical aid stations added.</div>'}

  <h2>Medical Transport (${medTransports.length})</h2>
  ${medTransports.length > 0 ? `<table>
    <thead><tr><th>Ambulance Service</th><th>Location</th><th>Contact</th><th>Level of Service</th><th>Air/Ground</th></tr></thead>
    <tbody>${medTransports.map(t => `<tr>
      <td>${t.ambulanceService || 'N/A'}</td><td>${t.location || 'N/A'}</td><td>${t.contact || 'N/A'}</td>
      <td><span class="badge badge-blue">${t.levelOfService || 'N/A'}</span></td>
      <td><span class="badge badge-purple">${t.airGround || 'Ground'}</span></td>
    </tr>`).join('')}</tbody>
  </table>` : '<div class="empty">No medical transport services added.</div>'}

  <h2>Hospitals (${hospitals.length})</h2>
  ${hospitals.length > 0 ? `<table>
    <thead><tr><th>Hospital</th><th>Address</th><th>Contact</th><th>Travel Time</th><th>Helipad</th><th>Burn Center</th><th>Trauma Center</th><th>Trauma Level</th></tr></thead>
    <tbody>${hospitals.map(h => `<tr>
      <td><strong>${h.hospitalName || 'N/A'}</strong></td><td>${h.address || 'N/A'}</td><td>${h.contactNumber || 'N/A'}</td>
      <td>${h.travelTime || 'N/A'}</td>
      <td class="${h.helipad === 'Yes' ? 'yes' : 'no'}">${h.helipad || 'No'}</td>
      <td class="${h.burnCenter === 'Yes' ? 'yes' : 'no'}">${h.burnCenter || 'No'}</td>
      <td class="${h.traumaCenter === 'Yes' ? 'yes' : 'no'}">${h.traumaCenter || 'No'}</td>
      <td>${h.traumaLevel || 'N/A'}</td>
    </tr>`).join('')}</tbody>
  </table>` : '<div class="empty">No hospitals added.</div>'}

  <h2>Medical Plan Entries (${medPlanEntries.length})</h2>
  ${medPlanEntries.length > 0 ? `<table>
    <thead><tr><th style="width:22%">Title</th><th style="width:18%">From (24h)</th><th style="width:18%">To (24h)</th><th>Plan</th></tr></thead>
    <tbody>${medPlanEntries.map(e => `<tr>
      <td><strong>${escapeHtml(e.title) || 'Untitled'}</strong></td>
      <td>${escapeHtml(fmt24(e.fromDateTime))}</td>
      <td>${escapeHtml(fmt24(e.toDateTime))}</td>
      <td style="white-space:pre-wrap">${escapeHtml(e.plan) || ''}</td>
    </tr>`).join('')}</tbody>
  </table>` : '<div class="empty">No medical plan entries added.</div>'}

  <div class="footer">
    <div><strong>ICS 206 - Medical Plan</strong></div>
    <div>Stations: ${medAidStations.length} | Transport: ${medTransports.length} | Hospitals: ${hospitals.length} | Plan Entries: ${medPlanEntries.length}</div>
    <div style="margin-top: 10px;">Powered by EXRSIM - Emergency Response Simulation Platform</div>
  </div>
</body></html>`;

    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  return (
    <div className="p-6 pb-12 space-y-6">
      {/* Title Card */}
      <Card className={`${theme.colors.cardBackground} ${theme.colors.border} border-orange-500/30`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-2xl font-bold text-orange-500 mb-1`}>ICS 206 - Medical Plan</h1>
              <p className={`${theme.colors.textPrimary} text-lg font-medium`}>{exerciseName}</p>
              <div className={`flex items-center gap-4 mt-2 ${theme.colors.textMuted} text-sm`}>
                <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> From: {startDate}</span>
                <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> To: {endDate}</span>
              </div>
            </div>
            <Button
              onClick={printICS206}
              variant="outline"
              className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              data-testid="print-ics-206-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print ICS 206
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ===== MEDICAL AID STATIONS ===== */}
      <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
        <CardHeader>
          <CardTitle className="text-orange-500 flex items-center gap-2">
            <Heart className="h-5 w-5" /> Medical Aid Stations ({medAidStations.length})
          </CardTitle>
          <CardDescription className={theme.colors.textMuted}>
            Medical aid station locations and personnel for incident medical support
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add/Edit Form */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700/50 space-y-3">
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wide">{editingMedAidStation ? 'Edit Station' : 'Add Station'}</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Station Name *</label>
                <Input value={currentMedAidStation.station} onChange={(e) => setCurrentMedAidStation(prev => ({...prev, station: e.target.value}))} placeholder="Station name" className="bg-gray-800 border-gray-600 text-white h-9 text-sm" />
              </div>
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Location</label>
                <Input value={currentMedAidStation.location} onChange={(e) => setCurrentMedAidStation(prev => ({...prev, location: e.target.value}))} placeholder="Location" className="bg-gray-800 border-gray-600 text-white h-9 text-sm" />
              </div>
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Contact</label>
                <Input value={currentMedAidStation.contact} onChange={(e) => setCurrentMedAidStation(prev => ({...prev, contact: e.target.value}))} placeholder="Contact info" className="bg-gray-800 border-gray-600 text-white h-9 text-sm" />
              </div>
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Paramedics On Site</label>
                <Select value={currentMedAidStation.paramedics || "No"} onValueChange={(value) => setCurrentMedAidStation(prev => ({...prev, paramedics: value}))}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white h-9 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    <SelectItem value="Yes" className="text-white focus:bg-gray-600">Yes</SelectItem>
                    <SelectItem value="No" className="text-white focus:bg-gray-600">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={addMedAidStation} size="sm" className="bg-orange-500 hover:bg-orange-600 text-white">
                <Plus className="h-3.5 w-3.5 mr-1" />{editingMedAidStation ? 'Update' : 'Add'} Station
              </Button>
              {editingMedAidStation && (
                <Button onClick={cancelEditMedAidStation} variant="outline" size="sm" className="border-gray-500 text-gray-300">
                  <X className="h-3.5 w-3.5 mr-1" />Cancel
                </Button>
              )}
            </div>
          </div>
          {/* List */}
          {medAidStations.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No medical aid stations added yet</div>
          ) : (
            <div className="space-y-2">
              {medAidStations.map(station => (
                <div key={station.id} className="bg-gray-800 rounded-lg p-3 border border-gray-700 flex justify-between items-center">
                  <div>
                    <h4 className="text-white font-semibold text-sm">{station.station}</h4>
                    <p className="text-gray-400 text-xs">{station.location} | Contact: {station.contact || 'N/A'}</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${station.paramedics === 'Yes' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      Paramedics: {station.paramedics || 'No'}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button onClick={() => editMedAidStation(station)} variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300 p-1 h-auto"><Edit className="h-4 w-4" /></Button>
                    <Button onClick={() => deleteMedAidStation(station.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300 p-1 h-auto"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== MEDICAL TRANSPORT ===== */}
      <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
        <CardHeader>
          <CardTitle className="text-orange-500 flex items-center gap-2">
            <Ambulance className="h-5 w-5" /> Medical Transport ({medTransports.length})
          </CardTitle>
          <CardDescription className={theme.colors.textMuted}>
            Ambulance services and medical transport resources
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add/Edit Form */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700/50 space-y-3">
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wide">{editingMedTransport ? 'Edit Transport' : 'Add Transport'}</p>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Ambulance Service *</label>
                <Input value={currentMedTransport.ambulanceService} onChange={(e) => setCurrentMedTransport(prev => ({...prev, ambulanceService: e.target.value}))} placeholder="Service name" className="bg-gray-800 border-gray-600 text-white h-9 text-sm" />
              </div>
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Location</label>
                <Input value={currentMedTransport.location} onChange={(e) => setCurrentMedTransport(prev => ({...prev, location: e.target.value}))} placeholder="Location" className="bg-gray-800 border-gray-600 text-white h-9 text-sm" />
              </div>
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Contact</label>
                <Input value={currentMedTransport.contact} onChange={(e) => setCurrentMedTransport(prev => ({...prev, contact: e.target.value}))} placeholder="Contact" className="bg-gray-800 border-gray-600 text-white h-9 text-sm" />
              </div>
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Level of Service</label>
                <Select value={currentMedTransport.levelOfService || ""} onValueChange={(value) => setCurrentMedTransport(prev => ({...prev, levelOfService: value}))}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white h-9 text-sm"><SelectValue placeholder="Select level" /></SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    {serviceLevels.map(l => <SelectItem key={l} value={l} className="text-white focus:bg-gray-600">{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Air/Ground</label>
                <Select value={currentMedTransport.airGround || "Ground"} onValueChange={(value) => setCurrentMedTransport(prev => ({...prev, airGround: value}))}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white h-9 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    <SelectItem value="Ground" className="text-white focus:bg-gray-600">Ground</SelectItem>
                    <SelectItem value="Air" className="text-white focus:bg-gray-600">Air</SelectItem>
                    <SelectItem value="Both" className="text-white focus:bg-gray-600">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={addMedTransport} size="sm" className="bg-orange-500 hover:bg-orange-600 text-white">
                <Plus className="h-3.5 w-3.5 mr-1" />{editingMedTransport ? 'Update' : 'Add'} Transport
              </Button>
              {editingMedTransport && (
                <Button onClick={cancelEditMedTransport} variant="outline" size="sm" className="border-gray-500 text-gray-300">
                  <X className="h-3.5 w-3.5 mr-1" />Cancel
                </Button>
              )}
            </div>
          </div>
          {/* List */}
          {medTransports.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No medical transport services added yet</div>
          ) : (
            <div className="space-y-2">
              {medTransports.map(transport => (
                <div key={transport.id} className="bg-gray-800 rounded-lg p-3 border border-gray-700 flex justify-between items-center">
                  <div>
                    <h4 className="text-white font-semibold text-sm">{transport.ambulanceService}</h4>
                    <p className="text-gray-400 text-xs">{transport.location} | {transport.contact || 'No contact'}</p>
                    <div className="flex gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400">{transport.levelOfService || 'N/A'}</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">{transport.airGround || 'Ground'}</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button onClick={() => editMedTransport(transport)} variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300 p-1 h-auto"><Edit className="h-4 w-4" /></Button>
                    <Button onClick={() => deleteMedTransport(transport.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300 p-1 h-auto"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== HOSPITALS ===== */}
      <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
        <CardHeader>
          <CardTitle className="text-orange-500 flex items-center gap-2">
            <Building2 className="h-5 w-5" /> Hospitals ({hospitals.length})
          </CardTitle>
          <CardDescription className={theme.colors.textMuted}>
            Hospital information for medical evacuations and patient care
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add/Edit Form */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700/50 space-y-3">
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wide">{editingHospital ? 'Edit Hospital' : 'Add Hospital'}</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Hospital Name *</label>
                <Input value={currentHospital.hospitalName} onChange={(e) => setCurrentHospital(prev => ({...prev, hospitalName: e.target.value}))} placeholder="Hospital name" className={`bg-gray-800 border-gray-600 text-white h-9 text-sm ${hospitalValidation?.hospitalName ? 'border-red-500' : ''}`} />
                {hospitalValidation?.hospitalName && <p className="text-red-500 text-xs mt-1">{hospitalValidation.hospitalName}</p>}
              </div>
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Address</label>
                <Input value={currentHospital.address} onChange={(e) => setCurrentHospital(prev => ({...prev, address: e.target.value}))} placeholder="Address" className="bg-gray-800 border-gray-600 text-white h-9 text-sm" />
              </div>
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Contact Number</label>
                <Input value={currentHospital.contactNumber} onChange={(e) => setCurrentHospital(prev => ({...prev, contactNumber: e.target.value}))} placeholder="Contact" className="bg-gray-800 border-gray-600 text-white h-9 text-sm" />
              </div>
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Travel Time</label>
                <Input value={currentHospital.travelTime} onChange={(e) => setCurrentHospital(prev => ({...prev, travelTime: e.target.value}))} placeholder="e.g., 15 min" className="bg-gray-800 border-gray-600 text-white h-9 text-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Helipad</label>
                <Select value={currentHospital.helipad || "No"} onValueChange={(value) => setCurrentHospital(prev => ({...prev, helipad: value}))}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    <SelectItem value="Yes" className="text-white focus:bg-gray-600">Yes</SelectItem>
                    <SelectItem value="No" className="text-white focus:bg-gray-600">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Burn Center</label>
                <Select value={currentHospital.burnCenter || "No"} onValueChange={(value) => setCurrentHospital(prev => ({...prev, burnCenter: value}))}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    <SelectItem value="Yes" className="text-white focus:bg-gray-600">Yes</SelectItem>
                    <SelectItem value="No" className="text-white focus:bg-gray-600">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Trauma Center</label>
                <Select value={currentHospital.traumaCenter || "No"} onValueChange={(value) => setCurrentHospital(prev => ({...prev, traumaCenter: value}))}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    <SelectItem value="Yes" className="text-white focus:bg-gray-600">Yes</SelectItem>
                    <SelectItem value="No" className="text-white focus:bg-gray-600">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-white text-xs font-medium mb-1 block">Trauma Level</label>
                <Select value={currentHospital.traumaLevel || ""} onValueChange={(value) => setCurrentHospital(prev => ({...prev, traumaLevel: value}))}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white h-9 text-sm"><SelectValue placeholder="Select level" /></SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    {traumaLevels.map(l => <SelectItem key={l} value={l} className="text-white focus:bg-gray-600">{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={addHospital} size="sm" className="bg-orange-500 hover:bg-orange-600 text-white">
                <Plus className="h-3.5 w-3.5 mr-1" />{editingHospital ? 'Update' : 'Add'} Hospital
              </Button>
              {editingHospital && (
                <Button onClick={cancelEditHospital} variant="outline" size="sm" className="border-gray-500 text-gray-300">
                  <X className="h-3.5 w-3.5 mr-1" />Cancel
                </Button>
              )}
            </div>
          </div>
          {/* List */}
          {hospitals.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No hospitals added yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 px-3 text-orange-500 font-semibold text-xs">Hospital</th>
                    <th className="text-left py-2 px-3 text-orange-500 font-semibold text-xs">Address</th>
                    <th className="text-left py-2 px-3 text-orange-500 font-semibold text-xs">Contact</th>
                    <th className="text-left py-2 px-3 text-orange-500 font-semibold text-xs">Travel</th>
                    <th className="text-left py-2 px-3 text-orange-500 font-semibold text-xs">Capabilities</th>
                    <th className="text-left py-2 px-3 text-orange-500 font-semibold text-xs">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {hospitals.map(hospital => (
                    <tr key={hospital.id} className="border-b border-gray-700 hover:bg-gray-800/50">
                      <td className="py-2 px-3 text-white font-semibold text-sm">{hospital.hospitalName}</td>
                      <td className="py-2 px-3 text-white text-sm">{hospital.address || 'N/A'}</td>
                      <td className="py-2 px-3 text-white text-sm">{hospital.contactNumber || 'N/A'}</td>
                      <td className="py-2 px-3 text-white text-sm">{hospital.travelTime || 'N/A'}</td>
                      <td className="py-2 px-3">
                        <div className="flex flex-wrap gap-1">
                          {hospital.helipad === 'Yes' && <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">Helipad</span>}
                          {hospital.burnCenter === 'Yes' && <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">Burn</span>}
                          {hospital.traumaCenter === 'Yes' && <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">{hospital.traumaLevel || 'Trauma'}</span>}
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex gap-1">
                          <Button onClick={() => editHospital(hospital)} variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300 p-1 h-auto"><Edit className="h-4 w-4" /></Button>
                          <Button onClick={() => deleteHospital(hospital.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300 p-1 h-auto"><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== MEDICAL PLAN ENTRIES ===== */}
      <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`} data-testid="med-plan-entries-card">
        <CardHeader>
          <CardTitle className="text-orange-500 flex items-center gap-2">
            <ClipboardList className="h-5 w-5" /> Medical Plan Entries ({medPlanEntries.length})
          </CardTitle>
          <CardDescription className={theme.colors.textMuted}>
            Time-bounded medical plan entries — each with a Title, From/To 24-hour date-times, and a free-text Plan
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add/Edit Form */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700/50 space-y-3">
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wide">
              {editingMedPlanEntry ? 'Edit Medical Plan Entry' : 'Add Medical Plan Entry'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-3">
                <label className="text-white text-xs font-medium mb-1 block">Title *</label>
                <Input
                  data-testid="med-plan-entry-title"
                  value={currentMedPlanEntry.title || ''}
                  onChange={(e) => setCurrentMedPlanEntry(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. Triage Protocol — OP-2"
                  className="bg-gray-800 border-gray-600 text-white h-9 text-sm"
                />
              </div>
              <div>
                <label className="text-white text-xs font-medium mb-1 block">From</label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    data-testid="med-plan-entry-from-date"
                    type="date"
                    value={currentMedPlanEntry.fromDateTime ? currentMedPlanEntry.fromDateTime.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = currentMedPlanEntry.fromDateTime ? currentMedPlanEntry.fromDateTime.split('T')[1] || '' : '';
                      setCurrentMedPlanEntry(prev => ({ ...prev, fromDateTime: date ? `${date}T${time}` : '' }));
                    }}
                    className="bg-gray-800 border-gray-600 text-white h-9 text-sm"
                  />
                  <Input
                    data-testid="med-plan-entry-from-time"
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={currentMedPlanEntry.fromDateTime ? currentMedPlanEntry.fromDateTime.split('T')[1] || '' : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) value = value + ':';
                      if (value.length <= 5) {
                        const date = currentMedPlanEntry.fromDateTime ? currentMedPlanEntry.fromDateTime.split('T')[0] : '';
                        setCurrentMedPlanEntry(prev => ({ ...prev, fromDateTime: date ? `${date}T${value}` : value }));
                      }
                    }}
                    className="bg-gray-800 border-gray-600 text-white h-9 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-white text-xs font-medium mb-1 block">To</label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    data-testid="med-plan-entry-to-date"
                    type="date"
                    value={currentMedPlanEntry.toDateTime ? currentMedPlanEntry.toDateTime.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = currentMedPlanEntry.toDateTime ? currentMedPlanEntry.toDateTime.split('T')[1] || '' : '';
                      setCurrentMedPlanEntry(prev => ({ ...prev, toDateTime: date ? `${date}T${time}` : '' }));
                    }}
                    className="bg-gray-800 border-gray-600 text-white h-9 text-sm"
                  />
                  <Input
                    data-testid="med-plan-entry-to-time"
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={currentMedPlanEntry.toDateTime ? currentMedPlanEntry.toDateTime.split('T')[1] || '' : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) value = value + ':';
                      if (value.length <= 5) {
                        const date = currentMedPlanEntry.toDateTime ? currentMedPlanEntry.toDateTime.split('T')[0] : '';
                        setCurrentMedPlanEntry(prev => ({ ...prev, toDateTime: date ? `${date}T${value}` : value }));
                      }
                    }}
                    className="bg-gray-800 border-gray-600 text-white h-9 text-sm"
                  />
                </div>
              </div>
              <div className="md:col-span-3">
                <label className="text-white text-xs font-medium mb-1 block">Plan</label>
                <Textarea
                  data-testid="med-plan-entry-plan"
                  value={currentMedPlanEntry.plan || ''}
                  onChange={(e) => setCurrentMedPlanEntry(prev => ({ ...prev, plan: e.target.value }))}
                  placeholder="Describe the medical plan, scope, intended actions, responsible parties, special instructions…"
                  rows={4}
                  className="bg-gray-800 border-gray-600 text-white text-sm resize-y"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="med-plan-entry-add-btn"
                onClick={addMedPlanEntry}
                size="sm"
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                {editingMedPlanEntry ? 'Update' : 'Add'} Entry
              </Button>
              {editingMedPlanEntry && (
                <Button
                  data-testid="med-plan-entry-cancel-btn"
                  onClick={cancelEditMedPlanEntry}
                  variant="outline"
                  size="sm"
                  className="border-gray-500 text-gray-300"
                >
                  <X className="h-3.5 w-3.5 mr-1" />Cancel
                </Button>
              )}
            </div>
          </div>

          {/* List */}
          {medPlanEntries.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No medical plan entries added yet</div>
          ) : (
            <div className="space-y-2">
              {medPlanEntries.map(entry => (
                <div
                  key={entry.id}
                  data-testid={`med-plan-entry-row-${entry.id}`}
                  className="bg-gray-800 rounded-lg p-3 border border-gray-700"
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <h4 className="text-white font-semibold text-sm truncate">
                        {entry.title || 'Untitled'}
                      </h4>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" /> From: <span className="text-gray-200">{fmt24(entry.fromDateTime)}</span>
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" /> To: <span className="text-gray-200">{fmt24(entry.toDateTime)}</span>
                        </span>
                      </div>
                      {entry.plan && (
                        <p className="text-gray-300 text-sm mt-2 whitespace-pre-wrap">{entry.plan}</p>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        onClick={() => editMedPlanEntry(entry)}
                        variant="ghost"
                        size="sm"
                        className="text-blue-400 hover:text-blue-300 p-1 h-auto"
                        data-testid={`med-plan-entry-edit-${entry.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => deleteMedPlanEntry(entry.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 p-1 h-auto"
                        data-testid={`med-plan-entry-delete-${entry.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ICS206MedicalPlan;
